import { Link, useNavigate, useOutletContext } from "react-router";
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { Button } from "@/components/button";
import { CountChip } from "@/components/count-chip";
import { Flag } from "@/components/flag";
import { isoCountryName } from "@/app/(app)/_lib/iso-countries";
import { Icon } from "@/components/icons";
import { PaginationFooter } from "@/components/pagination-footer";
import { ScoreChip } from "@/components/score-chip";
import { Tooltip } from "@/components/tooltip";
import { useToast } from "@/components/action-feedback";
import type { SourceId } from "@/app/(app)/screening/_data/types";
import { type ScreeningRun } from "../_data";
import {
  getCompanyConfig,
  getDpaSignature,
  enableDataRetention,
  type DataRetentionStatus,
  type DataRetentionPeriod,
} from "@/src/services/company_service";
import {
  getRecentSearches,
  RECENT_SEARCHES_PAGE_SIZE as ROWS_PER_PAGE,
} from "@/src/services/recent_searches_service";
import { toUserMessage } from "@/src/services/error_messages";
import { collapseKnownCollections, dataSourceLabel } from "@/components/watchlists";
import { EmptyInner } from "./empty-inner";
import { TableCardSkeleton } from "./skeletons";

type RecentColumnId =
  | "name"
  | "identifier"
  | "country"
  | "minScore"
  | "watchlist"
  | "seen"
  | "matches";

type RecentColumn = {
  id: RecentColumnId;
  header: string;
  width: string;
  cell: (run: ScreeningRun) => ReactNode;
};

const RECENT_COLUMNS_BY_ID: Record<RecentColumnId, RecentColumn> = {
  name: {
    id: "name",
    header: "Name",
    width: "minmax(120px,1fr)",
    cell: (run) => (
      <span className="flex h-12 min-w-0 items-center px-2 text-small text-black">
        {run.query ? (
          <Tooltip description={run.query} width="auto" wrapperClassName="min-w-0 truncate">
            {run.query}
          </Tooltip>
        ) : (
          <span className="text-grey-500">—</span>
        )}
      </span>
    ),
  },
  identifier: {
    id: "identifier",
    header: "Identifier",
    width: "minmax(100px,1fr)",
    cell: (run) => (
      <span className="flex h-12 min-w-0 items-center px-2 text-small text-black">
        {run.identifier ? (
          <Tooltip description={run.identifier} width="auto" wrapperClassName="min-w-0 truncate">
            {run.identifier}
          </Tooltip>
        ) : (
          <span className="text-grey-500">—</span>
        )}
      </span>
    ),
  },
  country: {
    id: "country",
    header: "Country",
    width: "88px",
    cell: (run) => (
      <span className="flex h-12 items-center px-2 text-small text-black">
        <CountryCountChips codes={run.countries ?? (run.countryIso ? [run.countryIso] : [])} />
      </span>
    ),
  },
  minScore: {
    id: "minScore",
    header: "Min. score",
    width: "88px",
    cell: (run) => (
      <span className="flex h-12 items-center px-2">
        <ScoreChip score={run.minScore} />
      </span>
    ),
  },
  watchlist: {
    id: "watchlist",
    header: "Watchlist",
    width: "120px",
    cell: (run) => (
      <span className="flex h-12 items-center px-2">
        <SourceListChips sources={sourceLabels(run)} />
      </span>
    ),
  },
  seen: {
    id: "seen",
    header: "Seen",
    width: "80px",
    cell: (run) => (
      <span className="flex h-12 items-center px-2 text-small text-grey-900">{run.seen}</span>
    ),
  },
  matches: {
    id: "matches",
    header: "Matches",
    width: "100px",
    cell: (run) => (
      <span className="flex h-12 items-center px-2">
        <CountChip count={run.hits} />
      </span>
    ),
  },
};

const RECENT_COLUMN_ORDER: RecentColumnId[] = [
  "name",
  "identifier",
  "country",
  "watchlist",
  "seen",
  "matches",
];
const RECENT_COLUMN_ORDER_WITH_SCORE: RecentColumnId[] = [
  "name",
  "identifier",
  "country",
  "minScore",
  "watchlist",
  "seen",
  "matches",
];

function recentColumns(showMinScore: boolean): RecentColumn[] {
  return (showMinScore ? RECENT_COLUMN_ORDER_WITH_SCORE : RECENT_COLUMN_ORDER).map(
    (id) => RECENT_COLUMNS_BY_ID[id],
  );
}

export function RecentScreeningsCard({
  empty = false,
  showMinScore = false,
  runs: runsData = null,
  count = 0,
  page = 1,
  onPageChange,
  pageFetching = false,
  dataRetentionStatus = "enabled",
  hasSignature = false,
  dataRetentionPeriod = "day",
  onDataRetentionEnabled,
}: {
  empty?: boolean;
  showMinScore?: boolean;
  runs?: ScreeningRun[] | null;
  count?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  pageFetching?: boolean;
  dataRetentionStatus?: DataRetentionStatus;
  hasSignature?: boolean;
  dataRetentionPeriod?: DataRetentionPeriod;
  onDataRetentionEnabled?: () => void;
} = {}) {
  const navigate = useNavigate();
  const runs: ScreeningRun[] = empty ? [] : (runsData ?? []);
  const isEmpty = runs.length === 0;
  const columns = useMemo(() => recentColumns(showMinScore), [showMinScore]);
  const gridTemplate = columns.map((c) => c.width).join(" ");

  const retentionGated = !empty && dataRetentionStatus !== "enabled";
  const pageCount = Math.max(1, Math.ceil(count / ROWS_PER_PAGE));
  return (
    <section className="card flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h3 className="text-body font-semibold text-black">Recent screenings</h3>
        <p className="text-small text-grey-600">
          History of recent screenings and number of matches
        </p>
      </header>

      <div className="flex flex-col overflow-x-auto rounded-sm border border-grey-200 bg-white shadow-flat">
        {retentionGated ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-10 text-center">
            <span className="flex size-10 items-center justify-center rounded-full bg-grey-100 text-grey-500">
              <Icon name="lock" size={20} />
            </span>
            <div className="flex flex-col gap-1">
              <p className="text-body font-semibold text-black">Recent searches are turned off</p>
              <p className="text-small text-grey-600">
                Viewing recent searches and matches requires{" "}
                <strong className="font-semibold text-grey-900">data retention</strong> to be
                enabled for your company. A company admin needs to opt in before search history is
                logged.
              </p>
            </div>
            <RequestDataRetentionButton
              status={dataRetentionStatus}
              hasSignature={hasSignature}
              period={dataRetentionPeriod}
              onEnabled={onDataRetentionEnabled}
            />
          </div>
        ) : (
          <>
            <div
              className={`flex flex-col transition-opacity ${pageFetching ? "pointer-events-none opacity-50" : ""}`}
              style={{ minHeight: 36 + ROWS_PER_PAGE * 48 }}
            >
              <div className="grid w-full" style={{ gridTemplateColumns: gridTemplate }}>
                {columns.map((col) => (
                  <TableHeaderCell key={col.id}>{col.header}</TableHeaderCell>
                ))}
              </div>
              {runs.map((run, i) => (
                <ScreeningRunRow
                  key={run.id}
                  run={run}
                  last={i === runs.length - 1}
                  columns={columns}
                  gridTemplate={gridTemplate}
                />
              ))}
              {isEmpty && (
                <EmptyInner
                  icon="search_off"
                  title="No recent screenings"
                  hint="Run a screening to see your history here."
                  action={
                    <Button
                      hierarchy="secondary"
                      size="sm"
                      leftIcon={<Icon name="search" size={20} />}
                      onClick={() => navigate("/screening-portal")}
                    >
                      Run screening
                    </Button>
                  }
                />
              )}
            </div>
            {pageCount > 1 && (
              <PaginationFooter
                compact
                total={count}
                individuals={0}
                organizations={0}
                others={0}
                page={page}
                pageCount={pageCount}
                onPageChange={onPageChange ?? (() => {})}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

export function RecentScreeningsSection({
  screening,
  previewMode,
  empty,
  showMinScore,
}: {
  screening: boolean;
  previewMode: boolean;
  empty: boolean;
  showMinScore: boolean;
}) {
  const ctx = useOutletContext<AppOutletContext | null>();
  const infoResolved = ctx?.infoResolved ?? false;
  const liveRecent = screening && !previewMode;
  // GET /company/contracts/ is OWNER/ADMIN-only on the backend — skip it for
  // every other role rather than firing a call that's just going to be
  // rejected (the result is only ever consumed by RequestDataRetentionButton,
  // which already hides itself for a non-manager).
  const canManageRetention =
    ctx?.apiUser?.accountType === "admin" || ctx?.apiUser?.accountType === "owner";

  const [page, setPage] = useState(1);
  const [runs, setRuns] = useState<ScreeningRun[] | null>(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageFetching, setPageFetching] = useState(false);
  const [dataRetentionStatus, setDataRetentionStatus] = useState<DataRetentionStatus | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [dataRetentionPeriod, setDataRetentionPeriod] = useState<DataRetentionPeriod>("day");
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!liveRecent) {
      setRuns(null);
      setCount(0);
      setLoading(false);
      setPageFetching(false);
      setDataRetentionStatus(null);
      setHasSignature(false);
      setPage(1);
      hasLoadedRef.current = false;
      return;
    }
    if (!infoResolved) {
      setLoading(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getCompanyConfig(),
      canManageRetention ? getDpaSignature() : Promise.resolve(null),
    ]).then(([configRes, signatureRes]) => {
      if (cancelled) return;
      const status = configRes.ok ? configRes.value.dataRetention : "disabled";
      setDataRetentionStatus(status);
      setHasSignature(signatureRes?.ok ?? false);
      if (configRes.ok) setDataRetentionPeriod(configRes.value.dataRetentionPeriod);
      if (status !== "enabled") {
        setRuns([]);
        setCount(0);
        setLoading(false);
        hasLoadedRef.current = true;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [liveRecent, infoResolved, canManageRetention]);

  function handleDataRetentionEnabled() {
    setDataRetentionStatus("enabled");
  }

  useEffect(() => {
    if (!liveRecent || dataRetentionStatus !== "enabled") return;
    let cancelled = false;
    if (hasLoadedRef.current) {
      setPageFetching(true);
    } else {
      setLoading(true);
    }
    getRecentSearches(page).then((res) => {
      if (cancelled) return;
      setRuns(res.ok ? res.value.runs : []);
      setCount(res.ok ? res.value.count : 0);
      setLoading(false);
      setPageFetching(false);
      hasLoadedRef.current = true;
    });
    return () => {
      cancelled = true;
    };
  }, [liveRecent, dataRetentionStatus, page]);

  if (loading) return <TableCardSkeleton />;
  return (
    <RecentScreeningsCard
      empty={empty}
      showMinScore={showMinScore}
      runs={runs}
      count={count}
      page={page}
      onPageChange={setPage}
      pageFetching={pageFetching}
      dataRetentionStatus={dataRetentionStatus ?? "enabled"}
      hasSignature={hasSignature}
      dataRetentionPeriod={dataRetentionPeriod}
      onDataRetentionEnabled={handleDataRetentionEnabled}
    />
  );
}

function RequestDataRetentionButton({
  status,
  hasSignature,
  period,
  onEnabled,
}: {
  status: DataRetentionStatus;
  hasSignature: boolean;
  period: DataRetentionPeriod;
  onEnabled?: () => void;
}) {
  const ctx = useOutletContext<AppOutletContext | null>();
  const navigate = useNavigate();
  const showToast = useToast();
  const [saving, setSaving] = useState(false);
  const canManage = ctx?.apiUser?.accountType === "admin" || ctx?.apiUser?.accountType === "owner";

  async function handleEnable() {
    setSaving(true);
    const res = await enableDataRetention(period);
    setSaving(false);
    if (!res.ok) {
      showToast({ tone: "error", text: toUserMessage(res.error) });
      return;
    }
    showToast({ tone: "success", text: "Data retention has been enabled." });
    onEnabled?.();
  }

  if (!canManage) return null;

  if (status === "disabled" && hasSignature) {
    return (
      <Button hierarchy="primary" size="sm" disabled={saving} onClick={() => void handleEnable()}>
        Enable data retention
      </Button>
    );
  }

  return (
    <Button
      hierarchy="primary"
      size="sm"
      onClick={() => navigate("/company-settings", { state: { openSignDpa: true } })}
    >
      {status === "requested" ? "View request status" : "Enable data retention"}
    </Button>
  );
}

function TableHeaderCell({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-9 items-center whitespace-nowrap border-b border-grey-200 bg-grey-50 px-2 text-small font-medium text-grey-900">
      {children}
    </span>
  );
}

function ScreeningRunRow({
  run,
  last,
  columns,
  gridTemplate,
}: {
  run: ScreeningRun;
  last: boolean;
  columns: RecentColumn[];
  gridTemplate: string;
}) {
  const borderClass = last ? "" : "border-b border-grey-100";
  const confPct = run.minScore > 0 ? Math.round(run.minScore * 100) : 88;
  const safeConf = confPct >= 80 && confPct <= 99 ? confPct : 88;
  const countries = run.countries ?? (run.countryIso ? [run.countryIso] : []);
  const searchParams = new URLSearchParams({ q: run.query, runAt: run.runAt });
  if (safeConf !== 88) searchParams.set("conf", String(safeConf));
  if (countries.length) searchParams.set("country", countries.join(","));
  if (run.entityType) searchParams.set("type", run.entityType);
  if (run.dob) searchParams.set("dob", run.dob);
  if (run.identifier) searchParams.set("identifier", run.identifier);
  if (run.watchlists?.length) searchParams.set("wl", run.watchlists.join(","));
  const href = `/screening-portal?${searchParams.toString()}`;
  return (
    <Link
      to={href}
      state={{ historicalMatches: run.results, historicalSearchId: run.searchId }}
      className={`grid bg-white hover:bg-grey-50 ${borderClass}`}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {columns.map((col) => (
        <Fragment key={col.id}>{col.cell(run)}</Fragment>
      ))}
    </Link>
  );
}

const LIST_LABELS: Record<SourceId, string> = {
  SDN: "OFAC",
  "OFAC-CONS": "OFAC",
  "UK-SANCTIONS": "HMT",
  CFSP: "EU-CSL",
  "EU-CONS": "EU-CSL",
  "CANADA-SEMA": "CA-SEMA",
  "SWISS-SECO": "CH-SECO",
  "UN-SC": "UN-SC",
  "AU-DFAT": "AU-DFAT",
  "INTERPOL-RED": "INT-RED",
  "FBI-WANTED": "FBI",
  "PEP-GLOBAL": "PEP",
};

function CountryCountChips({ codes }: { codes: string[] }) {
  if (codes.length === 0) return <span className="text-grey-500">—</span>;

  if (codes.length === 1) {
    const code = codes[0];
    return (
      <span className="inline-flex items-center gap-1.5" title={isoCountryName(code) || code}>
        <Flag iso={code} size={16} />
        <span>{code}</span>
      </span>
    );
  }

  return (
    <Tooltip
      align="start"
      description={
        <span className="flex flex-row flex-wrap gap-x-3 gap-y-1">
          {codes.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5">
              <Flag iso={c} size={14} />
              <span>{c}</span>
            </span>
          ))}
        </span>
      }
    >
      <span className="inline-flex items-center rounded-sm bg-grey-100 px-2 py-0.5 text-tiny font-medium text-grey-900">
        {codes.length}
      </span>
    </Tooltip>
  );
}

function sourceLabels(run: ScreeningRun): string[] {
  const rawIds = run.sourceIds ?? (run.topSource ? [run.topSource] : []);
  return collapseKnownCollections(rawIds).map(
    (id) => LIST_LABELS[id as SourceId] ?? dataSourceLabel(id),
  );
}

function SourceListChips({ sources }: { sources: string[] }) {
  if (sources.length === 0) {
    return (
      <span className="inline-flex items-center rounded-sm bg-grey-100 px-2 py-0.5 text-tiny font-medium text-grey-900">
        {sources.length}
      </span>
    );
  }

  if (sources.length === 1) {
    return (
      <span className="inline-flex items-center rounded-sm bg-grey-100 px-2 py-0.5 text-tiny font-medium text-grey-900">
        {sources[0]}
      </span>
    );
  }

  return (
    <Tooltip
      align="start"
      description={
        <span className="flex flex-row flex-wrap gap-x-3 gap-y-1">
          {sources.map((label) => (
            <span
              key={label}
              className="inline-flex items-center rounded-sm bg-grey-100 px-2 py-0.5 text-tiny font-medium text-grey-900"
            >
              {label}
            </span>
          ))}
        </span>
      }
    >
      <span className="inline-flex items-center gap-1">
        <span className="inline-flex items-center rounded-sm bg-grey-100 px-2 py-0.5 text-tiny font-medium text-grey-900">
          {sources.length}
        </span>
      </span>
    </Tooltip>
  );
}
